// Shared loading / empty / error surfaces. Every tab renders these instead of
// rolling its own spinner, dashed box, or red paragraph, so the three states
// read the same on every page and keep the visual tokens in one place.

import type { ReactNode } from 'react'
import clsx from 'clsx'
import { Button } from './Button'
import { IconAlert, IconInbox, IconRefresh } from './icons'
import css from './States.module.css'

export type LoadingSkeletonProps = {
  /** One-line Chinese status, e.g. “正在读取持仓结构…”. */
  label?: string
  /** page = full skeleton card block; inline = one-line list placeholder. */
  variant?: 'page' | 'inline'
  /** Number of skeleton rows for the page variant. */
  rows?: number
}

/**
 * Render the shared loading state.
 * @param props - status label, variant, row count.
 * @returns an aria-live skeleton block.
 */
export function LoadingSkeleton({ label = '正在加载…', variant = 'page', rows = 3 }: LoadingSkeletonProps) {
  if (variant === 'inline') {
    return (
      <div className={css.inline} role="status" aria-live="polite">
        <span className={css.spinner} aria-hidden="true" />
        <span>{label}</span>
      </div>
    )
  }
  return (
    <div className={css.page} role="status" aria-live="polite" aria-label={label}>
      <div className={css.skeletonGrid}>
        {Array.from({ length: rows }, (_, index) => (
          <div className={css.skeletonCard} key={index}>
            <span className={css.lineShort} />
            <span className={css.lineValue} />
            <span className={css.lineLong} />
          </div>
        ))}
      </div>
      <p className={css.pageLabel}>{label}</p>
    </div>
  )
}

export type EmptyStateProps = {
  /** Headline: one short Chinese sentence. */
  title: string
  /** Optional explanation. */
  description?: ReactNode
  /** Optional single action (usually a Button). */
  action?: ReactNode
  /** Override the default illustration. */
  illustration?: ReactNode
  className?: string
}

/**
 * Render the shared empty state.
 * @param props - title, optional description/action/illustration.
 * @returns a dashed empty surface with placeholder artwork.
 */
export function EmptyState({ title, description, action, illustration, className }: EmptyStateProps) {
  return (
    <div className={clsx(css.empty, className)}>
      <div className={css.illustration} aria-hidden="true">
        {illustration ?? (
          <>
            <span className={css.tile} />
            <span className={css.tile} />
            <span className={css.tileAccent}><IconInbox size={20} /></span>
          </>
        )}
      </div>
      <strong className={css.title}>{title}</strong>
      {description != null && <p className={css.description}>{description}</p>}
      {action != null && <div className={css.action}>{action}</div>}
    </div>
  )
}

export type ErrorStateProps = {
  /** Headline; defaults to “加载失败”. */
  title?: string
  /** Human-readable reason or retry hint. */
  description?: ReactNode
  /** Retry callback; renders the 重试 button when supplied. */
  onRetry?: () => void
  /** Retry button label. */
  retryLabel?: string
  /** page = centered card; inline = single row inside a list. */
  variant?: 'page' | 'inline'
  className?: string
}

/**
 * Render the shared error state.
 * @param props - title, description, retry callback, variant.
 * @returns an alert surface.
 */
export function ErrorState({
  title = '加载失败',
  description,
  onRetry,
  retryLabel = '重试',
  variant = 'page',
  className,
}: ErrorStateProps) {
  if (variant === 'inline') {
    return (
      <div className={clsx(css.errorInline, className)} role="alert">
        <IconAlert size={16} />
        <span>{description ?? title}</span>
        {onRetry && (
          <Button variant="ghost" size="sm" icon={<IconRefresh size={14} />} onClick={onRetry}>
            {retryLabel}
          </Button>
        )}
      </div>
    )
  }
  return (
    <div className={clsx(css.error, className)} role="alert">
      <span className={css.errorIcon}><IconAlert size={24} /></span>
      <strong className={css.title}>{title}</strong>
      {description != null && <p className={css.description}>{description}</p>}
      {onRetry && (
        <Button variant="outline" size="sm" icon={<IconRefresh size={14} />} onClick={onRetry}>
          {retryLabel}
        </Button>
      )}
    </div>
  )
}
